<template>
  <div class="checkout-container">
    <div class="checkout-container-left">
      <div
        v-if="invoice && invoice.invoice_items"
        class="invoice-items-container"
      >
        <div
          v-for="invoiceItem in invoiceFilteredItems"
          :key="invoiceItem.invoice_item_id"
          class="invoice-item"
        >
          <img
            class="invoice-item-thumbnail"
            :src="invoiceItem.image"
          >
          <p>{{ invoiceItem.description }}</p>
          <label>{{
            invoiceItem.display_hammer_price.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
      </div>

      <!-- Auction Items Total Bottom Section -->
      <div class="auction-item-total-container">
        <div>
          <label class="title">Lot Hammer</label>
          <label class="dollar-value">${{
            invoiceHammerTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div>
          <label class="title">Buyer's Premium</label>
          <label class="dollar-value">${{
            buyersPremiumTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div>
          <label class="title">Auction Items Total</label>
          <label class="dollar-value">${{
            itemsTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div>
          <p>*Total does not include applicable sales tax</p>
        </div>
      </div>
    </div>
    <div class="checkout-container-right">
      <!-- Top Header -->
      <div class="top-header-container">
        <img
          class="logo largeLogo"
          src="/images/dark-logo.svg"
          alt="Leland Little Auctions"
        >
        <label>AUCTION ITEM CHECKOUT</label>
      </div>

      <div class="details-container">
        <div class="breadcrumb-container">
          <div
            v-for="(crumb, index) in breadCrumbs"
            :key="crumb.title"
            class="breadcrumb-item"
          >
            <p
              :class="{
                'breadcrumb-item-active': crumb.active,
                'breadcrumb-item-clickable': !crumb.active && crumb.canNavigate,
                'breadcrumb-item-unclickable':
                  !crumb.active && !crumb.canNavigate
              }"
              @click="goToRoute(crumb.route, crumb.canNavigate)"
            >
              {{ crumb.title }}
            </p>
            <span v-if="index != breadCrumbs.length - 1"> / </span>
          </div>
        </div>

        <div class="details-inner-container">
          <div class="shipping-options-container">
            <h1>Shipping Options</h1>
            <p class="subtitle">
              Shipping to:
            </p>
            <p class="address">
              {{ customerAddress }}
            </p>

            <div class="checkout-form-container">
              <div class="options-container">
                <div
                  v-for="option in quoteCandidates"
                  :key="option.id"
                  class="shipping-option"
                >
                  <!-- Shipping Option Description -->
                  <div class="shipping-option-description">
                    <input
                      v-model="radioPicked"
                      type="radio"
                      :value="option.id"
                    >
                    <label class="description">{{ option.description }}</label>
                  </div>

                  <!-- Shipping Option Price -->
                  <div>
                    <label>${{
                      option.price.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })
                    }}</label>
                  </div>
                </div>

                <div class="shipping-option">
                  <!-- Shipping Option Description -->
                  <div class="shipping-option-description">
                    <input
                      type="radio"
                      @click="goToShipping()"
                    >
                    <label class="description">Pick Up</label>
                  </div>

                  <!-- Shipping Option Price -->
                  <div>
                    <label>$0.00</label>
                  </div>
                </div>
              </div>
              <button
                v-if="isThinking"
                class="disabledButton continue-btn"
                disabled="disabled"
              >
                <span
                  class="loading"
                  style="display: inline-block;"
                />
              </button>
              <button
                v-else
                class="continue-btn"
                @click="goToPaymentInfo()"
              >
                Continue
              </button>
            </div>

            <div
              v-if="quoteCandidates && quoteCandidates.length > 0"
              class="shipping-disclaimer"
            >
              <small>*Please provide 1-2 business days for ARTA to confirm your
                booking. Once confirmed, ARTA Parcel orders will take 3-5
                business days to be collected and packed, tracking will then be
                provided indicating delivery. ARTA Premium/Select orders will
                take 2-4 weeks from date of confirmation to delivery.</small>
              <div
                class="marginSmallTop navLink"
                @click="toggleModal()"
              >
                Why Choose Arta?
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Footer -->
      <div class="bottom-footer-container">
        <p
          v-if="isStaffView"
          class="staff-view-banner"
        >
          STAFF VIEW: Viewing invoice for {{ customerName }}
        </p>
        <div class="bottom-disclaimer">
          If you have any questions, please contact us here:
          <a href="mailto:info@lelandlittle.com">info@lelandlittle.com </a>
        </div>
        <button @click="exit()">
          Exit
        </button>
      </div>
    </div>
    <AlertComponent
      v-if="showAlert"
      :title="alertTitle"
      :message="alertMessage"
      :secondary-action="alertSecondaryAction"
      @close="exit()"
    />
    <AlertComponent
      v-show="error.show"
      :title="error.title"
      :message="error.message"
      @close="closeError()"
    />
    <ShippingOptionsInfoModalComponent
      :show-modal="showShippingOptionsInfoModal"
      :hide-modal="toggleModal"
    />
  </div>
</template>

<script>
import AlertComponent from '@/components/Shared/AlertComponent.vue'
import ShippingOptionsInfoModalComponent from '@/components/Checkout/ShippingOptions/ShippingOptionsInfoModalComponent.vue'
import apiMixin from '@/mixins/apiMixin.js'
import InvoiceValidateMixin from '@/mixins/InvoiceValidateMixin.js'
import { isEqual } from 'lodash'

export default {
	name: 'ShippingOptionsComponent',
	components: {
		AlertComponent,
        ShippingOptionsInfoModalComponent
	},
	mixins: [apiMixin, InvoiceValidateMixin],
	data () {
		return {
			error: {
				show: false,
				fatal: false,
				title: '',
				message: ''
			},
			radioPicked: 0,
			showAlert: false,
			alertTitle: '',
			alertMessage: 'If you have any questions, please contact us here: info@lelandlittle.com',
			alertSecondaryAction: null,
            showShippingOptionsInfoModal: false
		}
	},

	computed: {
		/**
		 * Get the current invoice id
		 * @public
		 */
		invoiceFilteredItems: function () {
			return this.invoice ? this.invoice.invoice_items.filter(item => item.inventory_id) : []
		},
		/**
		 * @public
		 */
		itemsTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_total_no_tax) {
				return parseFloat(invoice.invoice_total_no_tax).toFixed(2)
			}

			return parseFloat(0).toFixed(2)
		},
		/**
		 * @public
		 */
		buyersPremiumTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_buyers_total) {
				return invoice.invoice_buyers_total
			}

			return 0
		},
		/**
		 * @public
		 */
		invoiceHammerTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_hammer_total) {
				return invoice.invoice_hammer_total
			}

			return 0
		},
		/**
		 * @public
		 */
		customerAddress: function () {
			let address = this.$store.getters.shippingAddress

			let line1 = address.street_1
			let line2 = address.street_2 ? address.street_2 : ''
			let city = address.city
			let state = address.state
			let postal = address.zip

			// Line breaks didn't work first try, probably need to redo this
			let addressFinal = ''
			if (line2 !== '') {
				addressFinal = `${line1}\n${line2}`
			} else {
				addressFinal = `${line1}`
			}

			return `${addressFinal}\n${city}, ${state} ${postal}`
		},
		/**
		 * @public
		 */
		quoteCandidates: function () {
			const quotes = this.$store.getters.quoteCandidates
			const filteredQuotes = []
			// Make sure we are only showing Parcel quotes, then order by price, lowest to highest
			if (quotes && quotes.length > 0) {
				// Does a "Parcel" quote exist?
				let parcelQuoteExists = false
                let minimumParcelQuotePrice = 0;
				for (let i = 0; i < quotes.length; i++) {
					if (quotes[i].description.includes('Parcel')) {
						parcelQuoteExists = true
                        if (minimumParcelQuotePrice === 0) {
                            minimumParcelQuotePrice = quotes[i].price
                        }
                        if (quotes[i].price < minimumParcelQuotePrice) {
                            minimumParcelQuotePrice = quotes[i].price
                        }
					}
				}
				if (parcelQuoteExists === false) {
					// If no parcel quote exists, show all quotes sorted by price
					quotes.sort((a, b) => {
						return a.price - b.price
					})
					return quotes
				}
				quotes.forEach(quote => {
					if (!quote.description.includes('Premium') && !quote.description.includes('Select')) {
						filteredQuotes.push(quote)
					} else if (quote.price < minimumParcelQuotePrice) {
                        // It is Premium or Select and its price is lower than the lowest Parcel price
                        filteredQuotes.push(quote)
                    }
				})

				filteredQuotes.sort((a, b) => {
					return a.price - b.price
				})
				return filteredQuotes
			}

			return []
		},
		/**
		 * @public
		 */
		canGoToShipping: function () {
			let quoteCandidates = this.quoteCandidates
			if (!quoteCandidates.length) {
				return true
			}
			if (quoteCandidates[0].isCustom) {
				return false
			}
			return true
		},
		/**
		 * @public
		 */
		breadCrumbs: function () {
			return [
				{
					active: false,
					canNavigate: true,
					title: 'Auction Items',
					route: 'auctionItems'
				},
				{
					active: false,
					canNavigate: this.canGoToShipping,
					title: 'Shipping',
					route: 'shipping'
				},
				{
					active: true,
					canNavigate: false,
					title: 'Options',
					route: 'shippingOptions'
				},
				{
					active: false,
					canNavigate: false,
					title: 'Payment Info',
					route: 'paymentInfo'
				}
			]
		},
		/**
		 * @public
		 */
		currentInvoiceId: function () {
			return this.$store.getters.checkoutInvoiceId
		},
		/**
		 * @public
		 */
		invoices: function () {
			return this.$store.getters.invoices
		},
		/**
		 * @public
		 */
		invoice: function () {
			if (this.invoiceIsLoading) {
				return
			}
			const key = `INVOICEID_${this.currentInvoiceId}`
			const tempInvoice = this.invoices[key]
			// If tempInvoice is undefined we return false to trigger the invoice watcher
			return typeof tempInvoice !== 'undefined' ? tempInvoice : false
		},
		/**
		 * @public
		 */
		invoiceIsLoading: function () {
			return this.$store.state.invoiceIsLoading
		},
		/**
		 * @public
		 */
		isStaffView: function () {
			// If invoice customer id is not equal to logged in customer and logged in customer is staff
			// (api will not return invoice unless authenticated customer id is same as invoice id OR authenticated customer is staff)
			if (
				this.invoice &&
				this.invoice.customer_id &&
				this.$store.getters.customer.customer_id !== this.invoice.customer_id &&
				typeof this.apiResponse.get_customer_invoice !== 'undefined' &&
				typeof this.apiResponse.get_account !== 'undefined'
			) {
				return true
			}
			return false
		},
		/**
		 * @public
		 */
		customerName: function () {
			return this.invoice ? this.invoice.customer_display_name : ''
			// return this.$store.getters.customer.display_name
		},
		/**
		 * @public
		 */
		apiResponse: function () {
			return this.$store.getters.apiResponse
		},
		/**
		 * @public
		 */
		shippingAddress: function () {
			return this.$store.getters.shippingAddress
		},
		/**
		 * @public
		 */
		inventory_ids: function () {
			let tempInvoice = this.invoice
			let out = []
			if (tempInvoice && typeof tempInvoice.invoice_items === 'undefined') {
				return out
			}
			if (tempInvoice && tempInvoice.invoice_items) {
				for (let i = 0; i < tempInvoice.invoice_items.length; i++) {
					let item = tempInvoice.invoice_items[i]
					if (item.inventory_id) {
						out.push(item.inventory_id)
					}
				}
			}
			return out
		},
		/**
		 * @public
		 */
		isThinking: function () {
			if (this.error.show) {
				return true
			}
			let apiPending = this.$store.getters.apiPending
			if (typeof apiPending.get_customer_invoices !== 'undefined') {
				return true
			}
			if (typeof apiPending.arta_select_quote !== 'undefined') {
				return true
			}
			return false
		}
	},
	watch: {
		/**
		 * Watches the invoice object for changes
		 * @param {*} invoice
		 * @param {*} prevInvoice
		 */
		invoice: function (invoice, prevInvoice) {
			const isLoaded = !this.invoiceIsLoading
			if (!isEqual(prevInvoice, invoice)) {
				// Invoice changed, validate it
				if (isLoaded) {
					this.validateInvoice(invoice)
				}
			}
		}
	},

	mounted () {
		// If we don't have quote candidates on mounted, go back to start
		if (this.quoteCandidates.length === 0) {
			this.goToRoute('auctionItems', true)
		}
	},

	methods: {
        /**
         * @public
         */
        toggleModal() {
            this.showShippingOptionsInfoModal = !this.showShippingOptionsInfoModal
        },
		/**
		 * @public
		 */
		artaSelectQuote (quoteId) {
			// All of the quote candidates should have the same Arta request id.
			const artaRequest = this.quoteCandidates.length > 0 ? this.quoteCandidates[0].request : null
			const artaRequestId = artaRequest ? artaRequest.id : null
			var cmp = this
			var postData = {
				action: 'arta_select_quote',
				quote: {
					id: quoteId
				},
				request: {
					id: artaRequestId
				},
				shippingAddress: this.shippingAddress,
				inventory_ids: this.inventory_ids
			}

			var apiPayload = {}
			apiPayload.apiEndpoint = 'shipping'
			apiPayload.postData = postData
			var promise = this.$store
				.dispatch('apiCall', apiPayload)
				.then(response => {
					if (response.data.result !== 'success') {
						// No possibility of validation errors here. The only things that can go wrong here are technical errors on either frontend, backend, or with Arta.
						// The API should respond HTTP 403, result="failure" if anything doesn't go as planned.
						// cmp.openError("Something went wrong...", "Please try again later. If you see this more than once, please contact Leland Little for assistance.");
						cmp.openErrorCustomQuote(response.data.message)
						return false
					}
					let quoteCandidates = cmp.quoteCandidates
					for (let i = 0; i < quoteCandidates.length; i++) {
						quoteCandidates[i].selected = false
						if (quoteCandidates[i].id === quoteId) {
							quoteCandidates[i].selected = true
						}
					}
					this.$store.commit('UPDATE_QUOTE_CANDIDATES', quoteCandidates) // now that we've successfully saved quoteId to the database, push it up to store
					return true
				})
			return promise
		},
		/**
		 * @public
		 */
		reallyGoToPaymentInfo () {
			this.$store.dispatch('getAllCustomerShippingOrders')
			this.$router.push({ name: 'paymentInfo' })
		},
		/**
		 * @public
		 */
		goToPaymentInfo () {
			var cmp = this
			let quoteId = this.radioPicked
			if (quoteId < 1) {
				this.openError('No Quote Selected', 'Please select a quote.')
				return
			}
			cmp.artaSelectQuote(quoteId).then(success => {
				if (!success) {
					return
				}
				// update customer invoices to reflect new shipping selection before we go to the next page
				cmp.$store
					.dispatch('getCustomerInvoice', { invoiceId: this.currentInvoiceId })
					.then(response => {
						cmp.reallyGoToPaymentInfo()
					})
			})
		},
		/**
		 * @public
		 */
		goToRoute (route, canNavigate) {
			if (canNavigate) {
				this.$router.push({ name: route })
			}
		},
		/**
		 * @public
		 */
		goToShipping () {
			this.$router.push({ name: 'shipping', params: { radioPicked: 1 } })
		},
		/**
		 * @public
		 */
		openError (title, message) {
			this.error.title = ''
			this.error.message = ''
			if (typeof title === typeof 'asdf') {
				this.error.title = title
			}
			if (typeof message === typeof 'asdf') {
				this.error.message = message
			}
			this.error.show = true
		},
		/**
		 * @public
		 */
		openErrorCustomQuote (note) {
			this.error.title = 'Custom Shipping Quote Required'
			this.error.message =
				"This invoice requires a custom shipping quote. We'll notify you once your quote has been prepared."
			this.error.fatal = true
			this.error.show = true

			if (typeof note !== typeof 'asdf') {
				note = ''
			}
			var apiPayload = {
				apiEndpoint: 'shipping',
				postData: {
					action: 'custom_quote',
					inventory_ids: this.inventory_ids,
					note: note
				}
			}
			let shippingAddress = this.$store.getters.shippingAddress
			let shippingAddressKeys = Object.keys(this.$store.getters.shippingAddress)
			for (var i = 0; i < this.shippingAddressKeys.length; i++) {
				var field = shippingAddressKeys[i]
				apiPayload.postData[field] = shippingAddress[field]
			}
			this.$store.dispatch('apiCall', apiPayload)
		},
		/**
		 * Gets called when user clicks to close error AlertComponent
		 * @public
		 */
		closeError () {
			this.error.show = false
			if (this.error.fatal) {
				this.exit()
			}
		},
		/**
		 * Gets called when user clicks Exit button
		 * @public
		 */
		exit () {
			// apiMixin
			const referrer = localStorage.getItem('exit-referrer')
			if (referrer) {
				// If we have a exit-referrer, redirect to it
				window.location.replace(referrer)
				return
			}
			// Otherwise, redirect to the home page
			window.location.replace(this.getUrlHost(this.$store.getters.apiPath))
		}
	}
}
</script>

<style scoped></style>
